import * as THREE from 'three';

// ─── Constants ───────────────────────────────────────────────────────────────
const COURT_W = 18;
const COURT_H = 30;
const GOAL_W = 4;
const GOAL_H = 1.2;
const GOAL_INSET = 3.5;   // how far the goal mouth sits from the end wall

const HALF_W = COURT_W / 2;
const HALF_H = COURT_H / 2;

const PLAYER_RADIUS   = 0.62;
const GOALIE_RADIUS   = 0.78;
const BALL_RADIUS     = 0.28;
const PICKUP_RADIUS   = 1.1;
const SEPARATION_DIST = 1.5;

const FIELD_SPEED  = 3.5;
const GOALIE_SPEED = 2.25;
const PASS_SPEED   = 60.0;
const SHOT_SPEED   = 80.0;
const FRICTION     = 0.95;
const STOP_THRESH  = 0.05;

// AI tuning
const AI_FIELD_SPEED    = 2.9;   // slightly slower than player
const AI_GOALIE_SPEED   = 2.5;
const AI_SHOOT_ZONE_Y   = -7;    // Y below which AI will shoot at player goal
const AI_SHOOT_ACCURACY = 1.2;   // ± random offset on shot (lower = more accurate)
const AI_SHOOT_COOLDOWN = 2.5;   // seconds between AI shots
const AI_PRESSURE_DIST  = 3.5;   // distance at which the pursuer starts sprinting

// Shoot zone for player (Y above which player can shoot at AI goal)
const PLAYER_SHOOT_ZONE_Y = 2;

// Formation: home positions for player team (bottom half). Index 0 = goalie.
const PLAYER_FORMATION = [
    { x:    0, y: -HALF_H + GOAL_INSET + 0.8 },  // 0 — goalie (0.8 in front of goal mouth)
    { x:   -4, y: -6 },             // 1 — left defender
    { x:    4, y: -6 },             // 2 — right defender
    { x: -3.5, y: -2 },             // 3 — left forward
    { x:  3.5, y: -2 },             // 4 — right forward  ← starts with ball
];

// ─── GameEngine ──────────────────────────────────────────────────────────────
export default class GameEngine {
    constructor(container, options = {}) {
        this.container = container;
        this.callbacks  = options; // { onGoal, onSave, onCountdown }
        this._roster    = options.roster   ?? null;
        this._aiRoster  = options.aiRoster ?? null;

        // Three.js
        this.scene    = null;
        this.camera   = null;
        this.renderer = null;
        this.animFrameId = null;
        this._lastTime   = performance.now();

        // Game state
        this.score = { player: 0, ai: 0 };
        this._lastPlayerToucher = null;
        this._lastAIToucher     = null;
        this._paused    = false;
        this._resetting = false;      // true while faceoff reset is in progress
        this._resetTimer = 0;
        this._countdownActive = false;
        this._countdownValue  = 0;
        this._countdownTimer  = 0;
        this._goalParticles   = [];
        this._aiShootCooldown = 0;
        this._aiPassCooldown  = 0;
        this._aiGoalieClearTimer = -1; // -1 = not started
        // 1 = player attacks top (+Y), -1 = player attacks bottom (-Y) in 2nd half
        this._dir = 1;
        // Map<player, secondsRemaining> — prevents immediate re-pickup after pass/shot
        this._noCatch = new Map();
        this._time = 0;
        this._lastTapTime = 0;
        this._doubleTapDelay = 0.32; // seconds

        // Entities
        this.players   = [];
        this.aiPlayers = [];
        this.ballState = { pos: { x: 0, y: 0 }, vel: { x: 0, y: 0 }, carrier: null };

        // Visuals
        this._activeRing   = null;
        this._activeGroup  = null;
        this._activePlayer = null;
        this._ripples      = [];
        this.ballMesh      = null;

        // Phase 7 — Polish
        this._shake        = { timer: 0, intensity: 0 };
        this._celebrations = [];
        this._crowdDots    = { top: [], bottom: [] };
        this._audioCtx     = null;

        this._init();
    }

    // ─── Init ────────────────────────────────────────────────────────────────

    _init() {
        const w = this.container.clientWidth;
        const h = this.container.clientHeight;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x0a1628);

        const aspect   = w / h;
        const minW     = COURT_W + 2;   // always show 1 unit padding on each side
        const minH     = COURT_H + 6;
        // Fit both dimensions: expand whichever axis needs more room
        let viewH = minH;
        let viewW = viewH * aspect;
        if (viewW < minW) { viewW = minW; viewH = viewW / aspect; }
        this.camera  = new THREE.OrthographicCamera(
            -viewW / 2, viewW / 2, viewH / 2, -viewH / 2, 0.1, 100
        );
        this.camera.position.set(0, 0, 20);
        this.camera.lookAt(0, 0, 0);

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(w, h);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.appendChild(this.renderer.domElement);

        this.scene.add(new THREE.AmbientLight(0xffffff, 1.0));

        this._buildCourt();
        this._buildPlayers(this._roster, this._aiRoster);
        this._buildBallMesh();
        this._buildCrowdStrip();

        this._resizeHandler = () => this._onResize();
        window.addEventListener('resize', this._resizeHandler);

        this._tapHandler = (e) => this._onTap(e);
        this.renderer.domElement.addEventListener('pointerdown', this._tapHandler);

        this._animate();
        this._startCountdown();
    }

    // ─── Court ───────────────────────────────────────────────────────────────

    _buildCourt() {
        this.scene.add(new THREE.Mesh(
            new THREE.PlaneGeometry(COURT_W, COURT_H),
            new THREE.MeshBasicMaterial({ color: 0x1a4a7a })
        ));

        const lw = 0.18;
        this._addRect(0,        HALF_H,  COURT_W, lw,         0xffffff);
        this._addRect(0,       -HALF_H,  COURT_W, lw,         0xffffff);
        this._addRect(-HALF_W,  0,       lw,       COURT_H,   0xffffff);
        this._addRect( HALF_W,  0,       lw,       COURT_H,   0xffffff);
        this._addRect(0,        0,       COURT_W,  lw * 0.8,  0xdd2233);

        this.scene.add(this._makeRing(2.5, 2.7, 0xffffff, 0, 0, 0.01));

        const dot = new THREE.Mesh(
            new THREE.CircleGeometry(0.2, 16),
            new THREE.MeshBasicMaterial({ color: 0xffffff })
        );
        dot.position.z = 0.01;
        this.scene.add(dot);

        [[-5, 8], [5, 8], [-5, -8], [5, -8]].forEach(([x, y]) => {
            const d = new THREE.Mesh(
                new THREE.CircleGeometry(0.2, 16),
                new THREE.MeshBasicMaterial({ color: 0xdd2233 })
            );
            d.position.set(x, y, 0.01);
            this.scene.add(d);
        });

        const cornerR = 2;
        [
            [-HALF_W + cornerR,  HALF_H - cornerR, Math.PI / 2,      Math.PI],
            [ HALF_W - cornerR,  HALF_H - cornerR, 0,                 Math.PI / 2],
            [-HALF_W + cornerR, -HALF_H + cornerR, Math.PI,           3 * Math.PI / 2],
            [ HALF_W - cornerR, -HALF_H + cornerR, 3 * Math.PI / 2,   2 * Math.PI],
        ].forEach(([x, y, start, end]) => {
            const pts = [];
            for (let a = start; a <= end + 0.05; a += 0.08) {
                pts.push(new THREE.Vector3(
                    x + Math.cos(a) * cornerR,
                    y + Math.sin(a) * cornerR,
                    0.01
                ));
            }
            this.scene.add(new THREE.Line(
                new THREE.BufferGeometry().setFromPoints(pts),
                new THREE.LineBasicMaterial({ color: 0xffffff })
            ));
        });

        // Goals are inset GOAL_INSET units from end walls so players can skate behind them
        this._buildGoal(0,  HALF_H - GOAL_INSET + GOAL_H / 2, 'top');
        this._buildGoal(0, -HALF_H + GOAL_INSET - GOAL_H / 2, 'bottom');
        this._buildCrease(0,  HALF_H - GOAL_INSET, Math.PI, Math.PI);
        this._buildCrease(0, -HALF_H + GOAL_INSET, 0,       Math.PI);

        // Shoot zone highlight (dims in/out during play — stored for later control)
        this._shootZoneMesh = new THREE.Mesh(
            new THREE.PlaneGeometry(COURT_W - 1, 5),
            new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0, side: THREE.DoubleSide })
        );
        this._shootZoneMesh.position.set(0, HALF_H - GOAL_INSET, 0.005);
        this.scene.add(this._shootZoneMesh);
    }

    _buildGoal(x, y, side) {
        const net = new THREE.Mesh(
            new THREE.PlaneGeometry(GOAL_W, GOAL_H),
            new THREE.MeshBasicMaterial({ color: 0x888888, transparent: true, opacity: 0.6 })
        );
        net.position.set(x, y, 0.01);
        this.scene.add(net);

        [-GOAL_W / 2, GOAL_W / 2].forEach(px => {
            const post = new THREE.Mesh(
                new THREE.PlaneGeometry(0.22, GOAL_H + 0.1),
                new THREE.MeshBasicMaterial({ color: 0xffffff })
            );
            post.position.set(x + px, y, 0.03);
            this.scene.add(post);
        });

        const barY = side === 'top' ? y + GOAL_H / 2 : y - GOAL_H / 2;
        const bar  = new THREE.Mesh(
            new THREE.PlaneGeometry(GOAL_W + 0.22, 0.22),
            new THREE.MeshBasicMaterial({ color: 0xffffff })
        );
        bar.position.set(x, barY, 0.03);
        this.scene.add(bar);
    }

    _buildCrease(x, y, startAngle, endAngle) {
        const shape = new THREE.Shape();
        const r = 3;
        shape.moveTo(x - GOAL_W / 2, y);
        for (let a = startAngle; a <= endAngle + Math.PI + 0.05; a += 0.05) {
            shape.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        }
        shape.lineTo(x + GOAL_W / 2, y);
        const mesh = new THREE.Mesh(
            new THREE.ShapeGeometry(shape),
            new THREE.MeshBasicMaterial({ color: 0x4488cc, transparent: true, opacity: 0.25, side: THREE.DoubleSide })
        );
        mesh.position.z = 0.01;
        this.scene.add(mesh);
    }

    _addRect(x, y, w, h, color) {
        const mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(w, h),
            new THREE.MeshBasicMaterial({ color })
        );
        mesh.position.set(x, y, 0.01);
        this.scene.add(mesh);
        return mesh;
    }

    _makeRing(inner, outer, color, x, y, z) {
        const mesh = new THREE.Mesh(
            new THREE.RingGeometry(inner, outer, 48),
            new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
        );
        mesh.position.set(x, y, z);
        return mesh;
    }

    // ─── Players ─────────────────────────────────────────────────────────────

    _makeNumberTexture(number) {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width  = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.font = `bold ${String(number).length > 1 ? 26 : 32}px Arial, sans-serif`;
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(number), size / 2, size / 2);
        return new THREE.CanvasTexture(canvas);
    }

    _buildPlayers(playerRoster = null, aiRoster = null) {
        const POSITION_NAMES = ['Portero', 'Def. Izq', 'Def. Der', 'Del. Izq', 'Del. Der'];
        const usedNumbers = new Set();
        const pickNumber = () => {
            let n;
            do { n = Math.floor(Math.random() * 98) + 1; } while (usedNumbers.has(n));
            usedNumbers.add(n);
            return n;
        };

        PLAYER_FORMATION.forEach((home, i) => {
            const isGoalie = i === 0;

            // Player team
            const pData      = playerRoster && playerRoster[i];
            const pName      = pData ? pData.name   : POSITION_NAMES[i];
            const pNumber    = pData ? pData.number : pickNumber();
            const pSpeedMult = (!isGoalie && pData?.speed) ? 0.85 + (pData.speed - 1.0) * 0.30 : 1.0;
            const pPower     = pData?.power ?? 1.0;

            const pMesh = this._createPlayerMesh(0x2266ff, isGoalie, pNumber);
            pMesh.position.set(home.x, home.y, 0.05);
            this.scene.add(pMesh);
            this.players.push({
                mesh:    pMesh,
                pos:     { x: home.x, y: home.y },
                vel:     { x: 0, y: 0 },
                target:  { x: home.x, y: home.y },
                speed:   isGoalie ? GOALIE_SPEED : FIELD_SPEED * pSpeedMult,
                isGoalie,
                hasBall: false,
                homePos: { x: home.x, y: home.y },
                name:    pName,
                number:  pNumber,
                power:   pPower,
            });

            // AI team
            const aData   = aiRoster && aiRoster[i];
            const aName   = aData ? aData.name   : POSITION_NAMES[i];
            const aNumber = aData ? aData.number : pickNumber();

            const aMesh = this._createPlayerMesh(0xff2200, isGoalie, aNumber);
            aMesh.position.set(home.x, -home.y, 0.05);
            this.scene.add(aMesh);
            this.aiPlayers.push({
                mesh:    aMesh,
                pos:     { x: home.x, y: -home.y },
                vel:     { x: 0, y: 0 },
                target:  { x: home.x, y: -home.y },
                speed:   isGoalie ? AI_GOALIE_SPEED : AI_FIELD_SPEED,
                isGoalie,
                hasBall: false,
                homePos: { x: home.x, y: -home.y },
                name:    aName,
                number:  aNumber,
            });
        });

        // No initial carrier — first faceoff is contested
        this.ballState.pos     = { x: 0, y: 0 };
        this.ballState.carrier = null;
        this.players[4].pos    = { x: 0, y: -1.2 };
        this.players[4].target = { x: 0, y:  0 };
        this.aiPlayers[4].pos  = { x: 0, y:  1.2 };
        this.aiPlayers[4].target = { x: 0, y: 0 };
        this._setActive(this.players[4]);
    }

    _createPlayerMesh(color, isGoalie, number) {
        const group = new THREE.Group();
        const r = isGoalie ? GOALIE_RADIUS : PLAYER_RADIUS;

        group.add(new THREE.Mesh(
            new THREE.CircleGeometry(r, 20),
            new THREE.MeshBasicMaterial({ color })
        ));

        const outline = new THREE.Mesh(
            new THREE.RingGeometry(r, r + 0.12, 20),
            new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide })
        );
        outline.position.z = 0.001;
        group.add(outline);

        const inner = new THREE.Mesh(
            new THREE.CircleGeometry(r * 0.55, 16),
            new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(0.6).getHex() })
        );
        inner.position.z = 0.002;
        group.add(inner);

        if (number != null) {
            const numPlane = new THREE.Mesh(
                new THREE.PlaneGeometry(r * 1.1, r * 1.1),
                new THREE.MeshBasicMaterial({ map: this._makeNumberTexture(number), transparent: true })
            );
            numPlane.position.z = 0.003;
            group.add(numPlane);
        }

        return group;
    }

    _setActive(player) {
        if (this._activeRing && this._activeRing.parent) {
            this._activeRing.parent.remove(this._activeRing);
        }
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(0.85, 1.05, 24),
            new THREE.MeshBasicMaterial({ color: 0xffdd00, side: THREE.DoubleSide, transparent: true, opacity: 0.9 })
        );
        ring.position.z = 0.01;
        player.mesh.add(ring);
        this._activeRing   = ring;
        this._activeGroup  = player.mesh;
        this._activePlayer = player;
    }

    // ─── Ball mesh ────────────────────────────────────────────────────────────

    _buildBallMesh() {
        const group = new THREE.Group();
        group.add(new THREE.Mesh(
            new THREE.CircleGeometry(BALL_RADIUS, 16),
            new THREE.MeshBasicMaterial({ color: 0xff8800 })
        ));
        const outline = new THREE.Mesh(
            new THREE.RingGeometry(BALL_RADIUS, BALL_RADIUS + 0.08, 16),
            new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide })
        );
        outline.position.z = 0.001;
        group.add(outline);
        group.position.set(0, 0, 0.1);
        this.ballMesh = group;
        this.scene.add(group);
    }

    // ─── Physics ─────────────────────────────────────────────────────────────

    _updatePhysics(dt) {
        this._time += dt;
        this._movePlayers(this.players, dt);
        this._movePlayers(this.aiPlayers, dt);
        this._separatePlayers([...this.players, ...this.aiPlayers]);
        this._updateBall(dt);
        this._syncMeshes();
    }

    _movePlayers(team, dt) {
        team.forEach(p => {
            const dx   = p.target.x - p.pos.x;
            const dy   = p.target.y - p.pos.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 0.05) return;

            const move = Math.min(p.speed * dt, dist);
            p.pos.x += (dx / dist) * move;
            p.pos.y += (dy / dist) * move;

            if (p.isGoalie) {
                p.pos.x = Math.max(-GOAL_W / 2 - 0.5, Math.min(GOAL_W / 2 + 0.5, p.pos.x));
            } else {
                p.pos.x = Math.max(-HALF_W + PLAYER_RADIUS, Math.min(HALF_W - PLAYER_RADIUS, p.pos.x));
                p.pos.y = Math.max(-HALF_H + PLAYER_RADIUS, Math.min(HALF_H - PLAYER_RADIUS, p.pos.y));
            }
        });
    }

    _separatePlayers(all) {
        for (let i = 0; i < all.length; i++) {
            for (let j = i + 1; j < all.length; j++) {
                const a  = all[i];
                const b  = all[j];
                const dx = b.pos.x - a.pos.x;
                const dy = b.pos.y - a.pos.y;
                const d  = Math.sqrt(dx * dx + dy * dy);
                if (d < SEPARATION_DIST && d > 0.001) {
                    const push = (SEPARATION_DIST - d) / 2;
                    const nx   = dx / d;
                    const ny   = dy / d;
                    if (!a.isGoalie) { a.pos.x -= nx * push; a.pos.y -= ny * push; }
                    if (!b.isGoalie) { b.pos.x += nx * push; b.pos.y += ny * push; }
                }
            }
        }
    }

    _updateBall(dt) {
        const b = this.ballState;

        if (b.carrier) {
            b.pos.x = b.carrier.pos.x;
            b.pos.y = b.carrier.pos.y;
            return;
        }

        b.pos.x += b.vel.x * dt;
        b.pos.y += b.vel.y * dt;
        b.vel.x *= FRICTION;
        b.vel.y *= FRICTION;

        const spd = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
        if (spd < STOP_THRESH) { b.vel.x = 0; b.vel.y = 0; }

        // Wall bounce
        if (b.pos.x < -HALF_W + BALL_RADIUS) { b.pos.x = -HALF_W + BALL_RADIUS; b.vel.x *= -0.7; }
        if (b.pos.x >  HALF_W - BALL_RADIUS) { b.pos.x =  HALF_W - BALL_RADIUS; b.vel.x *= -0.7; }
        // Goals are inside the rink — always bounce off the end walls
        if (b.pos.y < -HALF_H + BALL_RADIUS) { b.pos.y = -HALF_H + BALL_RADIUS; b.vel.y *= -0.7; }
        if (b.pos.y >  HALF_H - BALL_RADIUS) { b.pos.y =  HALF_H - BALL_RADIUS; b.vel.y *= -0.7; }

        // Drain no-catch cooldowns
        this._noCatch.forEach((t, p) => {
            const remaining = t - dt;
            if (remaining <= 0) this._noCatch.delete(p);
            else this._noCatch.set(p, remaining);
        });

        // Pickup — skip players still on no-catch cooldown
        let closest = null;
        let closestDist = PICKUP_RADIUS;
        [...this.players, ...this.aiPlayers].forEach(p => {
            if (this._noCatch.has(p)) return;
            const dx = p.pos.x - b.pos.x;
            const dy = p.pos.y - b.pos.y;
            const d  = Math.sqrt(dx * dx + dy * dy);
            if (d < closestDist) { closestDist = d; closest = p; }
        });

        if (closest) {
            const ballSpd = spd; // capture before zeroing
            closest.hasBall = true;
            b.carrier = closest;
            b.vel.x = 0;
            b.vel.y = 0;
            // Freeze the new carrier in place — prevents stale support targets from pulling them away
            closest.target = { ...closest.pos };
            if (this.players.includes(closest)) {
                this._lastPlayerToucher = closest;
                this._setActive(closest);
            } else {
                this._lastAIToucher = closest;
            }
            // Save detection: goalie caught a fast-moving ball
            if (ballSpd > 20 && closest.isGoalie && this.callbacks.onSave) {
                this.callbacks.onSave({ side: this.players.includes(closest) ? 'player' : 'ai' });
            }
        }
    }

    _syncMeshes() {
        [...this.players, ...this.aiPlayers].forEach(p => {
            p.mesh.position.x = p.pos.x;
            p.mesh.position.y = p.pos.y;
        });
        this.ballMesh.position.x = this.ballState.pos.x;
        this.ballMesh.position.y = this.ballState.pos.y;
    }

    // ─── AI logic ────────────────────────────────────────────────────────────

    _updateAI(dt) {
        if (this._resetting) return;

        this._aiShootCooldown = Math.max(0, this._aiShootCooldown - dt);
        this._aiPassCooldown  = Math.max(0, this._aiPassCooldown  - dt);
        if (this._aiGoalieClearTimer > 0) this._aiGoalieClearTimer = Math.max(0, this._aiGoalieClearTimer - dt);

        const aiHasBall     = this.aiPlayers.some(p => p.hasBall);
        const playerHasBall = this.players.some(p => p.hasBall);

        if (aiHasBall) {
            this._aiOffense(dt);
        } else if (playerHasBall) {
            this._aiDefense();
        } else {
            // Loose ball — all AI field players move toward it
            this.aiPlayers.forEach(p => {
                if (p.isGoalie) return;
                p.target = { ...this.ballState.pos };
            });
        }

        this._updateGoalie(this.aiPlayers[0],  (HALF_H - GOAL_INSET - 0.8) * this._dir);
        this._updateGoalie(this.players[0],   -(HALF_H - GOAL_INSET - 0.8) * this._dir);
        this._updateActivePlayer();
        this._updatePlayerSupport();
        this._updateShootZone();
    }

    _updateActivePlayer() {
        // Always keep the player-team member closest to the puck as the active (user-controlled) player
        const ball = this.ballState.pos;
        let closest = null;
        let closestDist = Infinity;
        this.players.forEach(p => {
            const d = Math.hypot(p.pos.x - ball.x, p.pos.y - ball.y);
            if (d < closestDist) { closestDist = d; closest = p; }
        });
        if (closest && closest !== this._activePlayer) {
            this._setActive(closest);
        }
    }

    _aiOffense(dt) {
        const carrier = this.aiPlayers.find(p => p.hasBall);
        if (!carrier) return;

        // If the goalie has the ball, clear it to a field player instead of charging
        if (carrier.isGoalie) {
            this._aiGoalieClear();
            return;
        }

        // Ball carrier drives toward player goal
        const targetY = -(HALF_H - 2) * this._dir;
        carrier.target = { x: carrier.pos.x * 0.6, y: targetY };

        // Support players run ahead of carrier on flanks
        const fieldPlayers = this.aiPlayers.filter(p => !p.isGoalie && !p.hasBall);
        const d = this._dir;
        const clampY = v => Math.max(-HALF_H + 3, Math.min(HALF_H - 3, v));
        const supportPositions = [
            { x: -5, y: clampY(carrier.pos.y - 4 * d) },
            { x:  5, y: clampY(carrier.pos.y - 4 * d) },
            { x: -3, y: clampY(carrier.pos.y + 3 * d) },
            { x:  3, y: clampY(carrier.pos.y + 3 * d) },
        ];
        fieldPlayers.forEach((p, i) => {
            if (supportPositions[i]) p.target = { ...supportPositions[i] };
        });

        // Shoot when deep in range
        if (carrier.pos.y * this._dir < AI_SHOOT_ZONE_Y && this._aiShootCooldown <= 0) {
            this._aiShoot(carrier);
            return;
        }

        // Pass to a support player who is clearly further ahead toward player goal
        if (this._aiPassCooldown <= 0) {
            let bestTarget = null;
            let bestAdvantage = 3.5; // minimum forward advantage to bother passing
            fieldPlayers.forEach(p => {
                const dist = Math.hypot(p.pos.x - carrier.pos.x, p.pos.y - carrier.pos.y);
                if (dist > 9) return; // too far to pass reliably
                const advantage = (carrier.pos.y - p.pos.y) * this._dir; // positive = p is further toward goal
                if (advantage > bestAdvantage) { bestAdvantage = advantage; bestTarget = p; }
            });
            if (bestTarget) {
                this._aiPassTo(bestTarget, carrier);
                this._aiPassCooldown = 1.8;
            }
        }
    }

    _aiPassTo(target, passer) {
        const dx = target.pos.x - passer.pos.x;
        const dy = target.pos.y - passer.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        passer.hasBall = false;
        const b = this.ballState;
        b.carrier = null;
        b.vel.x = (dx / dist) * PASS_SPEED;
        b.vel.y = (dy / dist) * PASS_SPEED;
        this._noCatch.set(passer, 0.5);
    }

    _aiGoalieClear() {
        const goalie = this.aiPlayers[0];
        if (!goalie || !goalie.hasBall) return;

        const fieldPlayers = this.aiPlayers.filter(p => !p.isGoalie);
        if (fieldPlayers.length === 0) return;

        // Move teammates close to the goalie to create short passing options
        const receiveOffsets = [
            { x: -3.5, y: -3 * this._dir },
            { x:  3.5, y: -3 * this._dir },
            { x: -2,   y: -6 * this._dir },
            { x:  2,   y: -6 * this._dir },
        ];
        fieldPlayers.forEach((p, i) => {
            if (receiveOffsets[i]) {
                const rawY = goalie.pos.y + receiveOffsets[i].y;
                p.target = {
                    x: Math.max(-HALF_W + 1, Math.min(HALF_W - 1, goalie.pos.x + receiveOffsets[i].x)),
                    y: this._dir > 0 ? Math.max(0, rawY) : Math.min(0, rawY),
                };
            }
        });

        // Start the wait timer once (-1 = not yet started)
        if (this._aiGoalieClearTimer < 0) this._aiGoalieClearTimer = 1.2;

        // Wait until teammates have moved closer before passing
        if (this._aiGoalieClearTimer > 0) return;

        this._aiGoalieClearTimer = -1; // reset sentinel for next time

        // Pass to the nearest field player (who should now be close)
        let target = fieldPlayers[0];
        let bestDist = Infinity;
        fieldPlayers.forEach(p => {
            const d = Math.hypot(p.pos.x - goalie.pos.x, p.pos.y - goalie.pos.y);
            if (d < bestDist) { bestDist = d; target = p; }
        });

        const dx   = target.pos.x - goalie.pos.x;
        const dy   = target.pos.y - goalie.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        goalie.hasBall = false;
        const b = this.ballState;
        b.carrier = null;
        b.vel.x   = (dx / dist) * PASS_SPEED;
        b.vel.y   = (dy / dist) * PASS_SPEED;
        this._noCatch.set(goalie, 1.5);
    }

    _aiDefense() {
        const carrier = this.ballState.carrier;

        // If player goalie has the ball, give them space to start play
        if (carrier && carrier.isGoalie && this.players.includes(carrier)) {
            this._aiGiveSpaceToGoalie();
            return;
        }

        const ball = this.ballState.pos;

        // Sort field players by distance to ball
        const fieldPlayers = this.aiPlayers.filter(p => !p.isGoalie);
        fieldPlayers.sort((a, b) => {
            const da = Math.hypot(a.pos.x - ball.x, a.pos.y - ball.y);
            const db = Math.hypot(b.pos.x - ball.x, b.pos.y - ball.y);
            return da - db;
        });

        // Nearest pursues ball carrier
        if (fieldPlayers[0]) {
            fieldPlayers[0].target = { ...ball };
            // Sprint boost when close
            const d = Math.hypot(fieldPlayers[0].pos.x - ball.x, fieldPlayers[0].pos.y - ball.y);
            fieldPlayers[0].speed = d < AI_PRESSURE_DIST
                ? AI_FIELD_SPEED * 1.25
                : AI_FIELD_SPEED;
        }

        // Others take defensive cover positions (drop toward AI goal)
        const coverPositions = [
            { x: -4, y: ball.y + 4 * this._dir },
            { x:  4, y: ball.y + 4 * this._dir },
            { x:  0, y: ball.y + 7 * this._dir },
        ];
        fieldPlayers.slice(1).forEach((p, i) => {
            if (coverPositions[i]) {
                p.target = { ...coverPositions[i] };
                p.speed  = AI_FIELD_SPEED;
            }
        });
    }

    _aiGiveSpaceToGoalie() {
        // AI spreads across its own half with gentle oscillation while player goalie distributes
        const t = this._time;
        const lanes = [-6, -2, 2, 6];
        const fieldPlayers = this.aiPlayers.filter(p => !p.isGoalie);
        fieldPlayers.forEach((p, i) => {
            const lane = lanes[i] !== undefined ? lanes[i] : (i % 2 === 0 ? -4 : 4);
            const phase = i * (Math.PI / 2);
            const ox = Math.sin(t * 0.6 + phase) * 1.5;
            const oy = Math.sin(t * 0.45 + phase + 1) * 1.2;
            const yd = (9 + oy) * this._dir;
            p.target = {
                x: Math.max(-HALF_W + 1, Math.min(HALF_W - 1, lane + ox)),
                y: this._dir > 0
                    ? Math.max(5, Math.min(HALF_H - 3, yd))
                    : Math.min(-5, Math.max(-(HALF_H - 3), yd)),
            };
            p.speed = AI_FIELD_SPEED;
        });
    }

    _updateGoalie(goalie, homeY) {
        if (!goalie) return;
        if (goalie.hasBall) return; // Let offense logic handle positioning when goalie has ball
        // Track ball X position, stay on goal line Y
        const targetX = Math.max(-GOAL_W / 2, Math.min(GOAL_W / 2, this.ballState.pos.x));
        goalie.target = { x: targetX, y: homeY };
    }

    _updatePlayerSupport() {
        const playerHasBall = this.players.some(p => p.hasBall);
        const aiHasBall     = this.aiPlayers.some(p => p.hasBall);

        // Player goalie has ball — field players spread across player half with gentle movement
        const playerGoalieHasBall = this.players[0] && this.players[0].hasBall;
        if (playerGoalieHasBall) {
            const t = this._time;
            const lanes = [-6, -2, 2, 6];
            const fieldPlayers = this.players.filter(p => !p.isGoalie);
            fieldPlayers.forEach((p, i) => {
                const lane = lanes[i] !== undefined ? lanes[i] : (i % 2 === 0 ? -4 : 4);
                const phase = i * (Math.PI / 2);
                const ox = Math.sin(t * 0.7 + phase) * 1.2;
                const oy = Math.sin(t * 0.5 + phase + 1) * 1.0;
                const pd = -this._dir; // -1 when player half is bottom, 1 when top
                const baseY = 6 * pd + oy;
                p.target = {
                    x: Math.max(-HALF_W + 1, Math.min(HALF_W - 1, lane + ox)),
                    y: pd < 0
                        ? Math.max(-HALF_H + 3, Math.min(-3, baseY))
                        : Math.max(3, Math.min(HALF_H - 3, baseY)),
                };
            });
            return;
        }

        // If AI goalie has ball, player team backs off to give space
        const aiGoalieHasBall = this.aiPlayers[0] && this.aiPlayers[0].hasBall;
        if (aiGoalieHasBall) {
            const backOffPositions = [
                { x: -4, y: -4 * this._dir },
                { x:  4, y: -4 * this._dir },
                { x: -2, y: -2 * this._dir },
                { x:  2, y: -2 * this._dir },
            ];
            const fieldPlayers = this.players.filter(p => !p.isGoalie && p !== this._activePlayer);
            fieldPlayers.forEach((p, i) => {
                if (backOffPositions[i]) p.target = { ...backOffPositions[i] };
            });
            return;
        }

        // Loose ball — all player field players actively chase it
        if (!playerHasBall && !aiHasBall) {
            this.players.forEach(p => {
                if (p.isGoalie) return;
                p.target = { ...this.ballState.pos };
            });
            return;
        }

        // AI has ball — non-active players apply pressure and hold shape
        // Active player target is NOT overridden so single-tap movement works
        if (aiHasBall) {
            const ball = this.ballState.pos;
            const fieldPlayers = this.players.filter(p => !p.isGoalie && p !== this._activePlayer);
            fieldPlayers.sort((a, b) => {
                const da = Math.hypot(a.pos.x - ball.x, a.pos.y - ball.y);
                const db = Math.hypot(b.pos.x - ball.x, b.pos.y - ball.y);
                return da - db;
            });
            if (fieldPlayers[0]) fieldPlayers[0].target = { ...ball };
            const coverPositions = [
                { x: -4, y: ball.y - 4 * this._dir },
                { x:  4, y: ball.y - 4 * this._dir },
                { x:  0, y: ball.y - 7 * this._dir },
            ];
            fieldPlayers.slice(1).forEach((p, i) => {
                if (coverPositions[i]) p.target = { ...coverPositions[i] };
            });
            return;
        }

        // Player has ball — teammates make constant dynamic runs toward AI goal
        const carrier = this._activePlayer;
        if (!carrier || carrier.isGoalie) return;

        const t = this._time;
        const fieldPlayers = this.players.filter(p => !p.isGoalie && p !== carrier);
        // Each teammate gets a lane and oscillates within it, always pushing toward AI goal
        const lanes = [-6, -3, 3, 6];
        fieldPlayers.forEach((p, i) => {
            const lane = lanes[i] !== undefined ? lanes[i] : (i % 2 === 0 ? -4 : 4);
            const phaseOffset = i * (Math.PI * 2 / Math.max(fieldPlayers.length, 1));
            const oscillateX = Math.sin(t * 1.1 + phaseOffset) * 2.0;
            const targetX = Math.max(-HALF_W + 1, Math.min(HALF_W - 1, lane + oscillateX));
            // Stagger forward positions so they don't all stack on the same Y
            const forwardOffset = (i % 2) * 3;
            const advance = (4 + forwardOffset) * this._dir;
            const targetY = Math.min(HALF_H - 2,
                Math.max(-HALF_H + 2, carrier.pos.y + advance));
            p.target = { x: targetX, y: targetY };
        });
    }

    _aiShoot(shooter) {
        this._playSound('shoot');
        const b  = this.ballState;
        const targetX = (Math.random() - 0.5) * AI_SHOOT_ACCURACY * 2;
        const targetY = -(HALF_H - GOAL_INSET + GOAL_H / 2) * this._dir; // center of player goal

        const dx   = targetX - shooter.pos.x;
        const dy   = targetY - shooter.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        shooter.hasBall = false;
        b.carrier = null;
        b.vel.x   = (dx / dist) * SHOT_SPEED;
        b.vel.y   = (dy / dist) * SHOT_SPEED;

        this._noCatch.set(shooter, 1.0);
        this._aiShootCooldown = AI_SHOOT_COOLDOWN;
    }

    // ─── Goal detection ──────────────────────────────────────────────────────

    _checkGoals() {
        if (this._resetting) return;
        const b = this.ballState;

        // Only count goals from free balls (not carried) — prevents scoring by skating behind net
        if (b.carrier !== null) return;

        // Score only when ball is inside the net volume (mouth → back wall) AND moving inward.
        // Upper bound prevents counting shots that go around/behind the net.
        // Velocity direction check prevents counting balls bouncing back out of the net zone.
        const goalMouthY = HALF_H - GOAL_INSET;
        const inAIGoal     = b.pos.y * this._dir >  goalMouthY
                          && b.pos.y * this._dir <  goalMouthY + GOAL_H
                          && Math.abs(b.pos.x) < GOAL_W / 2
                          && b.vel.y * this._dir > 0;
        const inPlayerGoal = b.pos.y * this._dir < -goalMouthY
                          && b.pos.y * this._dir > -(goalMouthY + GOAL_H)
                          && Math.abs(b.pos.x) < GOAL_W / 2
                          && b.vel.y * this._dir < 0;

        if (inAIGoal) {
            this.score.player++;
            this._spawnGoalParticles(b.pos.x, b.pos.y, 'player');
            const t = this._lastPlayerToucher;
            if (t) this._startCelebration(t);
            if (this.callbacks.onGoal) this.callbacks.onGoal({
                scorer: 'player',
                score: { ...this.score },
                scorerName:   t ? t.name   : null,
                scorerNumber: t ? t.number : null,
            });
            this._startReset();
        } else if (inPlayerGoal) {
            this.score.ai++;
            this._spawnGoalParticles(b.pos.x, b.pos.y, 'ai');
            const t = this._lastAIToucher;
            if (t) this._startCelebration(t);
            if (this.callbacks.onGoal) this.callbacks.onGoal({
                scorer: 'ai',
                score: { ...this.score },
                scorerName:   t ? t.name   : null,
                scorerNumber: t ? t.number : null,
            });
            this._startReset();
        }
    }

    // ─── Goal particles ──────────────────────────────────────────────────────

    _spawnGoalParticles(x, y, scorer) {
        this._triggerShake(0.45);
        this._triggerCrowdCelebration(scorer);
        this._playSound('goal');
        const primary = scorer === 'player' ? 0x2266ff : 0xff2200;
        const palette = [primary, 0xffffff, 0xffdd00];
        for (let i = 0; i < 25; i++) {
            const angle = (Math.PI * 2 * i) / 25 + (Math.random() - 0.5) * 0.5;
            const speed = 9 + Math.random() * 12;
            const color = palette[Math.floor(Math.random() * palette.length)];
            const size  = 0.28 + Math.random() * 0.38;
            const mesh  = new THREE.Mesh(
                new THREE.CircleGeometry(size, 6),
                new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1.0 })
            );
            mesh.position.set(x, y, 0.2);
            this.scene.add(mesh);
            this._goalParticles.push({
                mesh,
                vel: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
                life: 1.0,
            });
        }
    }

    _updateGoalParticles(dt) {
        for (let i = this._goalParticles.length - 1; i >= 0; i--) {
            const p = this._goalParticles[i];
            p.life -= dt * 0.6;
            p.mesh.position.x += p.vel.x * dt;
            p.mesh.position.y += p.vel.y * dt;
            p.vel.x *= 0.90;
            p.vel.y *= 0.90;
            p.mesh.material.opacity = Math.max(0, p.life);
            if (p.life <= 0) {
                this.scene.remove(p.mesh);
                this._goalParticles.splice(i, 1);
            }
        }
    }

    // ─── Countdown ───────────────────────────────────────────────────────────

    _startCountdown() {
        this._countdownActive = true;
        this._countdownValue  = 3;
        this._countdownTimer  = 1.0;
        if (this.callbacks.onCountdown) this.callbacks.onCountdown(3);
    }

    _tickCountdown(dt) {
        this._countdownTimer -= dt;
        if (this._countdownTimer <= 0) {
            this._countdownValue--;
            if (this._countdownValue <= 0) {
                this._countdownActive = false;
                if (this.callbacks.onCountdown) this.callbacks.onCountdown(0); // 0 = GO!
            } else {
                this._countdownTimer = 1.0;
                if (this.callbacks.onCountdown) this.callbacks.onCountdown(this._countdownValue);
            }
        }
    }

    _startReset() {
        this._resetting = true;
        this._resetTimer = 2.0; // 2 second pause before faceoff

        // Freeze ball
        this.ballState.vel.x = 0;
        this.ballState.vel.y = 0;
        this.ballState.carrier = null;
        this.players.forEach(p => { p.hasBall = false; });
        this.aiPlayers.forEach(p => { p.hasBall = false; });
    }

    _tickReset(dt) {
        if (!this._resetting) return;
        this._resetTimer -= dt;
        if (this._resetTimer <= 0) {
            this._resetToFaceoff();
        }
    }

    _resetToFaceoff() {
        this._resetting = false;

        // Move all players back to home positions (respecting current half's side)
        this.players.forEach((p, i) => {
            const home = { x: PLAYER_FORMATION[i].x, y: PLAYER_FORMATION[i].y * this._dir };
            p.pos    = { ...home };
            p.target = { ...home };
            p.vel    = { x: 0, y: 0 };
            p.hasBall = false;
        });
        this.aiPlayers.forEach((p, i) => {
            const home = { x: PLAYER_FORMATION[i].x, y: -PLAYER_FORMATION[i].y * this._dir };
            p.pos    = { ...home };
            p.target = { ...home };
            p.vel    = { x: 0, y: 0 };
            p.hasBall = false;
        });

        // Ball at center — contested faceoff, no pre-assigned carrier
        this.ballState.pos     = { x: 0, y: 0 };
        this.ballState.vel     = { x: 0, y: 0 };
        this.ballState.carrier = null;
        this._noCatch.clear();

        // Position one player from each side at the faceoff dot, targeting the puck
        this.players[4].pos    = { x: 0, y: -1.2 * this._dir };
        this.players[4].target = { x: 0, y: 0 };
        this.aiPlayers[4].pos    = { x: 0, y:  1.2 * this._dir };
        this.aiPlayers[4].target = { x: 0, y: 0 };

        // Highlight the faceoff player so the user sees who they're controlling
        this._setActive(this.players[4]);
        this._aiShootCooldown = 0;
        this._aiPassCooldown  = 0;
        this._aiGoalieClearTimer = -1;

        this._startCountdown();
    }

    // ─── Shoot zone indicator ─────────────────────────────────────────────────

    _updateShootZone() {
        if (!this._shootZoneMesh || !this._activePlayer) return;
        const inZone = this._activePlayer.pos.y * this._dir > PLAYER_SHOOT_ZONE_Y && !this._activePlayer.isGoalie;
        const target = inZone ? 0.06 : 0;
        const current = this._shootZoneMesh.material.opacity;
        this._shootZoneMesh.material.opacity += (target - current) * 0.1;
        // Keep shoot zone mesh near the attacking goal
        this._shootZoneMesh.position.y = (HALF_H - GOAL_INSET) * this._dir;
    }

    // ─── Tap handling ────────────────────────────────────────────────────────

    _onTap(e) {
        e.preventDefault();
        if (!this._audioCtx) {
            try { this._audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch (_) {}
        }
        if (this._resetting || this._paused || this._countdownActive) return;

        const worldPos = this._screenToWorld(e.clientX, e.clientY);
        if (!worldPos) return;

        const now = performance.now() / 1000;
        const isDoubleTap = (now - this._lastTapTime) < this._doubleTapDelay;
        this._lastTapTime = now;

        const ap = this._activePlayer;

        if (isDoubleTap && ap && ap.hasBall) {
            // Double tap with ball → shoot if tapping at/near the attacking goal, otherwise pass
            if (worldPos.y * this._dir > HALF_H - 1.5) {
                this._playerShoot(worldPos.x);
                this._spawnRipple(worldPos.x, worldPos.y);
                return;
            }

            // Try to pass to the nearest teammate to the tap point
            let passTarget = null;
            let passTargetDist = 4.5;
            this.players.forEach(p => {
                if (p === ap || p.isGoalie) return;
                const d = Math.hypot(worldPos.x - p.pos.x, worldPos.y - p.pos.y);
                if (d < passTargetDist) { passTargetDist = d; passTarget = p; }
            });

            if (passTarget) {
                this._passBall(passTarget);
            } else {
                this._passToSpace(worldPos.x, worldPos.y);
            }

            this._spawnRipple(worldPos.x, worldPos.y);
            return;
        }

        // Single tap with ball → check if tapping on a teammate to pass
        if (ap && ap.hasBall) {
            let clickedTeammate = null;
            let minDist = PLAYER_RADIUS + 0.8;
            this.players.forEach(p => {
                if (p === ap || p.isGoalie) return;
                const d = Math.hypot(worldPos.x - p.pos.x, worldPos.y - p.pos.y);
                if (d < minDist) { minDist = d; clickedTeammate = p; }
            });
            if (clickedTeammate) {
                this._passBall(clickedTeammate);
                this._spawnRipple(worldPos.x, worldPos.y);
                return;
            }
        }

        // Single tap on empty space → move active player there
        this._moveActiveTo(worldPos.x, worldPos.y);
        this._spawnRipple(worldPos.x, worldPos.y);
    }

    _passToSpace(x, y) {
        const b = this.ballState;
        if (!b.carrier) return;
        this._playSound('pass');

        const dx   = x - b.pos.x;
        const dy   = y - b.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        const passer = b.carrier;
        passer.hasBall = false;
        b.carrier = null;
        b.vel.x   = (dx / dist) * PASS_SPEED;
        b.vel.y   = (dy / dist) * PASS_SPEED;

        // Prevent passer from immediately reclaiming the ball; they follow into the space
        this._noCatch.set(passer, 0.8);
        passer.target = {
            x: Math.max(-HALF_W + PLAYER_RADIUS, Math.min(HALF_W - PLAYER_RADIUS, x)),
            y: Math.max(-HALF_H + PLAYER_RADIUS, Math.min(HALF_H - PLAYER_RADIUS, y)),
        };
    }

    _playerShoot(tapX) {
        const b = this.ballState;
        if (!b.carrier || !this._activePlayer) return;
        this._triggerShake(0.15);
        this._playSound('shoot');

        // Aim X is the tap X clamped to just inside the goal posts + small corners
        const aimX = Math.max(-GOAL_W / 2 - 0.3, Math.min(GOAL_W / 2 + 0.3, tapX));
        const aimY = (HALF_H - GOAL_INSET + GOAL_H / 2) * this._dir; // center of AI goal

        const dx   = aimX - b.pos.x;
        const dy   = aimY - b.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        const shooter = this._activePlayer;
        shooter.hasBall = false;
        b.carrier = null;
        b.vel.x   = (dx / dist) * SHOT_SPEED;
        b.vel.y   = (dy / dist) * SHOT_SPEED;

        // Prevent shooter from immediately picking the ball back up
        this._noCatch.set(shooter, 1.0);
    }

    _passBall(target) {
        const b = this.ballState;
        if (!b.carrier) return;
        this._playSound('pass');

        const dx   = target.pos.x - b.pos.x;
        const dy   = target.pos.y - b.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        const passer = b.carrier;
        passer.hasBall = false;
        b.carrier = null;
        b.vel.x   = (dx / dist) * PASS_SPEED;
        b.vel.y   = (dy / dist) * PASS_SPEED;

        // Prevent passer from immediately catching the ball back
        this._noCatch.set(passer, 0.5);
    }

    _moveActiveTo(x, y) {
        if (!this._activePlayer) return;
        this._activePlayer.target = {
            x: Math.max(-HALF_W + PLAYER_RADIUS, Math.min(HALF_W - PLAYER_RADIUS, x)),
            y: Math.max(-HALF_H + PLAYER_RADIUS, Math.min(HALF_H - PLAYER_RADIUS, y)),
        };
    }

    _screenToWorld(screenX, screenY) {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const ndcX =  ((screenX - rect.left) / rect.width)  * 2 - 1;
        const ndcY = -((screenY - rect.top)  / rect.height) * 2 + 1;
        const vec  = new THREE.Vector3(ndcX, ndcY, 0);
        vec.unproject(this.camera);
        return { x: vec.x, y: vec.y };
    }

    // ─── Ripple effect ───────────────────────────────────────────────────────

    _spawnRipple(x, y) {
        const mesh = new THREE.Mesh(
            new THREE.RingGeometry(0.1, 0.22, 24),
            new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
        );
        mesh.position.set(x, y, 0.15);
        this.scene.add(mesh);
        this._ripples.push({ mesh, life: 1.0 });
    }

    _updateRipples(dt) {
        for (let i = this._ripples.length - 1; i >= 0; i--) {
            const r = this._ripples[i];
            r.life -= dt * 2.5;
            const s = 1 + (1 - r.life) * 3;
            r.mesh.scale.set(s, s, 1);
            r.mesh.material.opacity = r.life * 0.7;
            if (r.life <= 0) {
                this.scene.remove(r.mesh);
                this._ripples.splice(i, 1);
            }
        }
    }

    // ─── Phase 7: Screen shake ────────────────────────────────────────────────

    _triggerShake(intensity) {
        this._shake.timer     = 0.35;
        this._shake.intensity = intensity;
    }

    // ─── Phase 7: Player celebration ─────────────────────────────────────────

    _startCelebration(player) {
        // Prevent duplicate celebration entries for the same player
        if (this._celebrations.some(c => c.player === player)) return;
        this._celebrations.push({ player, timer: 1.1 });
    }

    _updateCelebrations(dt) {
        for (let i = this._celebrations.length - 1; i >= 0; i--) {
            const c = this._celebrations[i];
            c.timer -= dt;
            if (c.timer <= 0) {
                c.player.mesh.rotation.z = 0;
                this._celebrations.splice(i, 1);
            } else {
                c.player.mesh.rotation.z += dt * 13; // ~2 full spins per second
            }
        }
    }

    // ─── Phase 7: Crowd particle strip ───────────────────────────────────────

    _buildCrowdStrip() {
        const CROWD_COLORS = [0xff2200, 0x2266ff, 0xffdd00, 0xffffff, 0xff8800, 0x44ff88];
        const COUNT = 28;
        const step  = COURT_W / COUNT;

        [1, -1].forEach(side => {
            const baseY = side * (HALF_H + GOAL_H + 0.75);
            const arr   = side > 0 ? this._crowdDots.top : this._crowdDots.bottom;
            for (let i = 0; i < COUNT; i++) {
                const x     = -HALF_W + step * i + step / 2;
                const color = CROWD_COLORS[Math.floor(Math.random() * CROWD_COLORS.length)];
                const mesh  = new THREE.Mesh(
                    new THREE.CircleGeometry(0.27, 8),
                    new THREE.MeshBasicMaterial({ color })
                );
                mesh.position.set(x, baseY, 0.04);
                this.scene.add(mesh);
                arr.push({ mesh, baseY, velY: 0 });
            }
        });
    }

    _triggerCrowdCelebration(scorer) {
        // In 1st half (dir=1): player scores at top goal, AI at bottom goal
        // In 2nd half (dir=-1): player scores at bottom goal, AI at top goal
        const side = scorer === 'player'
            ? (this._dir > 0 ? 'top' : 'bottom')
            : (this._dir > 0 ? 'bottom' : 'top');
        const dir = side === 'top' ? 1 : -1;
        this._crowdDots[side].forEach(d => {
            d.velY = dir * (3 + Math.random() * 4);
        });
    }

    _updateCrowdDots(dt) {
        [...this._crowdDots.top, ...this._crowdDots.bottom].forEach(d => {
            if (Math.abs(d.velY) < 0.01 && Math.abs(d.mesh.position.y - d.baseY) < 0.02) return;
            d.mesh.position.y += d.velY * dt;
            // Spring back to base
            const dy = d.baseY - d.mesh.position.y;
            d.velY += dy * 22 * dt;
            d.velY *= 0.86;
            if (Math.abs(d.velY) < 0.04 && Math.abs(dy) < 0.04) {
                d.mesh.position.y = d.baseY;
                d.velY = 0;
            }
        });
    }

    // ─── Phase 7: Sound hooks ─────────────────────────────────────────────────

    _playSound(type) {
        if (!this._audioCtx) return;
        if (this._audioCtx.state === 'suspended') {
            this._audioCtx.resume().catch(() => {});
        }
        const ctx = this._audioCtx;
        const now = ctx.currentTime;

        if (type === 'pass') {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(520, now);
            osc.frequency.exponentialRampToValueAtTime(280, now + 0.07);
            gain.gain.setValueAtTime(0.12, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
            osc.start(now);
            osc.stop(now + 0.1);
        } else if (type === 'shoot') {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(180, now);
            osc.frequency.exponentialRampToValueAtTime(70, now + 0.18);
            gain.gain.setValueAtTime(0.18, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
            osc.start(now);
            osc.stop(now + 0.22);
        } else if (type === 'goal') {
            [0, 0.13, 0.26].forEach((offset, i) => {
                const osc  = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'square';
                osc.frequency.setValueAtTime(440 * (1 + i * 0.3), now + offset);
                gain.gain.setValueAtTime(0.15, now + offset);
                gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.11);
                osc.start(now + offset);
                osc.stop(now + offset + 0.13);
            });
        }
    }

    // ─── Resize ──────────────────────────────────────────────────────────────

    _onResize() {
        const w = this.container.clientWidth;
        const h = this.container.clientHeight;
        const aspect   = w / h;
        const minW     = COURT_W + 2;
        const minH     = COURT_H + 6;
        let viewH = minH;
        let viewW = viewH * aspect;
        if (viewW < minW) { viewW = minW; viewH = viewW / aspect; }
        this.camera.left   = -viewW / 2;
        this.camera.right  =  viewW / 2;
        this.camera.top    =  viewH / 2;
        this.camera.bottom = -viewH / 2;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h);
    }

    // ─── Animation loop ──────────────────────────────────────────────────────

    _animate() {
        this.animFrameId = requestAnimationFrame(() => this._animate());

        const now = performance.now();
        const dt  = Math.min((now - this._lastTime) / 1000, 0.05);
        this._lastTime = now;

        if (!this._paused) {
            if (this._resetting) {
                this._tickReset(dt);
                this._syncMeshes();
            } else if (this._countdownActive) {
                this._tickCountdown(dt);
                this._syncMeshes();
            } else {
                this._updatePhysics(dt);
                this._updateAI(dt);
                this._checkGoals();
            }
        }

        this._updateRipples(dt);
        this._updateGoalParticles(dt);
        this._updateCelebrations(dt);
        this._updateCrowdDots(dt);

        // Screen shake
        if (this._shake.timer > 0) {
            this._shake.timer -= dt;
            const amt = this._shake.intensity * Math.max(0, this._shake.timer / 0.35);
            this.camera.position.x = (Math.random() - 0.5) * amt * 2;
            this.camera.position.y = (Math.random() - 0.5) * amt * 2;
        } else {
            this.camera.position.x = 0;
            this.camera.position.y = 0;
        }

        // Pulse active ring
        if (this._activeRing) {
            const t = now * 0.004;
            const s = 1 + 0.18 * Math.sin(t);
            this._activeRing.scale.set(s, s, 1);
            this._activeRing.material.opacity = 0.5 + 0.4 * Math.abs(Math.sin(t));
        }

        this.renderer.render(this.scene, this.camera);
    }

    // ─── Public API ──────────────────────────────────────────────────────────

    getScore() {
        return { ...this.score };
    }

    isInShootZone() {
        return !!(this._activePlayer
            && !this._activePlayer.isGoalie
            && this._activePlayer.hasBall
            && this._activePlayer.pos.y * this._dir > PLAYER_SHOOT_ZONE_Y);
    }

    shoot() {
        if (this._resetting || this._paused || this._countdownActive) return;
        const b = this.ballState;
        if (!b.carrier || b.carrier !== this._activePlayer) return;
        if (!this.isInShootZone()) return;
        this._playerShoot(b.pos.x);
        this._spawnRipple(b.pos.x, b.pos.y);
    }

    getActivePlayerInfo() {
        if (!this._activePlayer) return null;
        return { name: this._activePlayer.name, number: this._activePlayer.number };
    }

    pause() {
        this._paused = true;
    }

    resume() {
        this._paused = false;
        this._lastTime = performance.now(); // avoid dt spike after pause
    }

    resetForSecondHalf() {
        this.score = { player: this.score.player, ai: this.score.ai }; // preserve score
        this._dir = -1; // swap sides: player now attacks bottom, defends top
        this._resetToFaceoff();
        this._paused = false;
    }

    destroy() {
        if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
        window.removeEventListener('resize', this._resizeHandler);
        this.renderer.domElement.removeEventListener('pointerdown', this._tapHandler);
        this.renderer.dispose();
        if (this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
    }
}
